import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Grid, Typography, Link, Divider, Stack, Chip } from '@mui/material';
import {
  Description as DescriptionIcon,
  CalendarMonth as CalendarIcon,
  Payment as PaymentIcon,
  Security as SecurityIcon,
  Comment as CommentIcon,
} from '@mui/icons-material';
import { InfoCard, InfoItem, formatNumber } from './CommonComponents';

/**
 * 개선된 수주품의서 기본정보 표시 컴포넌트 (V2)
 * 
 * 개선사항:
 * - 섹션별 카드 분리
 * - 라벨-값 세로 배치로 가독성 향상
 * - 반응형 그리드 적용
 * - 아이콘으로 섹션 구분
 */
const OrderBookInfoV2 = ({ orderInfo, project, isLink = false }) => {
  // 프로젝트 링크 URL 생성
  const linkUrl =
    orderInfo && orderInfo?.ordYn === 'Y'
      ? `/projectmng/${project?.prjUuid}/${project?.cmpGrade}/1`
      : `/chancemng/${project?.prjUuid}/${project?.cstGrade}/1`;

  // 프로젝트 번호 렌더링 (링크 여부에 따라)
  const renderPrjNo = isLink ? (
    <Link 
      component={RouterLink} 
      to={linkUrl}
      sx={{ 
        fontWeight: 600,
        textDecoration: 'none',
        '&:hover': { textDecoration: 'underline' }
      }}
    >
      {orderInfo?.prjNo}
    </Link>
  ) : (
    <Typography variant="body1" fontWeight={600} color="primary.main">
      {orderInfo?.prjNo}
    </Typography>
  );

  return (
    <Box>
      {/* 프로젝트 기본 정보 */}
      <InfoCard title="프로젝트 정보" icon={<DescriptionIcon />}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Typography 
              variant="caption" 
              color="text.secondary" 
              sx={{ mb: 0.5, display: 'block', fontWeight: 500 }}
            >
              프로젝트 번호
            </Typography>
            {renderPrjNo}
          </Grid>
          <InfoItem 
            label="프로젝트명" 
            value={orderInfo?.prjNm} 
            xs={12} sm={6} md={3}
            highlight
          />
          <InfoItem 
            label="매출처" 
            value={orderInfo?.partnerNm} 
            xs={12} sm={6} md={3}
          />
          <InfoItem 
            label="영업담당자" 
            value={orderInfo?.crtNm} 
            xs={12} sm={6} md={3}
          />
        </Grid>
      </InfoCard>

      {/* 계약 정보 */}
      <InfoCard title="계약 정보" icon={<CalendarIcon />}>
        <Grid container spacing={3}>
          <InfoItem 
            label="계약일" 
            value={orderInfo?.expDay} 
            xs={12} sm={6} md={3}
          />
          <InfoItem 
            label="계약완료일" 
            value={orderInfo?.siDt} 
            xs={12} sm={6} md={3}
          />
          <InfoItem 
            label="납품예정일" 
            value={orderInfo?.dlvryDuedate} 
            xs={12} sm={6} md={3}
          />
          <InfoItem 
            label="검수조건" 
            value={orderInfo?.inspectCond} 
            xs={12} sm={6} md={3}
          />
        </Grid>

        {/* 무상유지보수 기간 (신규 프로젝트인 경우만) */}
        {project?.prjTyp === 'N' && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
              무상 유지보수
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  sx={{ mb: 0.5, display: 'block', fontWeight: 500 }}
                >
                  유지보수 기간
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body1">
                    {orderInfo?.mtnFreeStrtDt || '-'} ~ {orderInfo?.mtnFreeEndDt || '-'}
                  </Typography>
                  {orderInfo?.mtnFreeTm && (
                    <Chip 
                      label={`${orderInfo.mtnFreeTm}개월`} 
                      size="small" 
                      color="info" 
                      variant="outlined"
                    />
                  )}
                </Stack>
              </Grid>
            </Grid>
          </>
        )}

        {/* 라이센스 기간 */}
        {(orderInfo?.licStrtDt || orderInfo?.licEndDt) && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
              라이센스
            </Typography>
            <Grid container spacing={3}>
              <InfoItem 
                label="라이센스 기간" 
                value={`${orderInfo?.licStrtDt || '-'} ~ ${orderInfo?.licEndDt || '-'}`}
                xs={12} sm={6} md={4}
              />
            </Grid>
          </>
        )}
      </InfoCard>

      {/* 결제 조건 */}
      <InfoCard title="결제 조건" icon={<PaymentIcon />}>
        <Grid container spacing={3}>
          <InfoItem 
            label="수금조건" 
            value={orderInfo?.collCondt} 
            xs={12} sm={6} md={6}
          />
          <InfoItem 
            label="지급조건" 
            value={orderInfo?.payCondt} 
            xs={12} sm={6} md={6}
          />
        </Grid>
      </InfoCard>

      {/* 보증보험 (값이 있는 경우만) */}
      {(orderInfo?.giPrePct || orderInfo?.giCovPct || orderInfo?.giMtnPct) && (
        <InfoCard title="보증보험" icon={<SecurityIcon />}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4} md={4}>
              <Box sx={{ 
                p: 2, 
                bgcolor: 'primary.50', 
                borderRadius: 1,
                textAlign: 'center',
              }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  선급금 비율
                </Typography>
                <Typography variant="h5" color="primary.main" fontWeight={700}>
                  {orderInfo?.giPrePct || 0}%
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4} md={4}>
              <Box sx={{ 
                p: 2, 
                bgcolor: 'success.50', 
                borderRadius: 1,
                textAlign: 'center',
              }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  계약이행 비율
                </Typography>
                <Typography variant="h5" color="success.main" fontWeight={700}>
                  {orderInfo?.giCovPct || 0}%
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4} md={4}>
              <Box sx={{ 
                p: 2, 
                bgcolor: 'warning.50', 
                borderRadius: 1,
                textAlign: 'center',
              }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  하자유지보수 비율
                </Typography>
                <Typography variant="h5" color="warning.main" fontWeight={700}>
                  {orderInfo?.giMtnPct || 0}%
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </InfoCard>
      )}

      {/* 참고사항 */}
      {orderInfo?.comment && (
        <InfoCard title="참고사항" icon={<CommentIcon />}>
          <Typography 
            variant="body2" 
            sx={{ 
              whiteSpace: 'pre-line',
              lineHeight: 1.8,
              color: 'text.secondary',
            }}
          >
            {orderInfo.comment}
          </Typography>
        </InfoCard>
      )}
    </Box>
  );
};

export default OrderBookInfoV2;
